// src/components/Navbar.tsx
import React, { useState } from 'react';
import {
  ShoppingCart,
  Heart,
  Search,
  Menu,
  X,
  Phone,
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Logo1 from '../logo/Logo_shynerz.png'; // Import the logo

interface NavbarProps {
  onCartClick: () => void;
  onWishlistClick: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  onCartClick,
  onWishlistClick,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { state, dispatch } = useApp();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're in standalone mode (PWA)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone;

  const cartItemsCount = state.cart.reduce(
    (total, item) => total + item.quantity,
    0
  );
  const wishlistCount = state.wishlist.length;

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch({ type: 'SET_SEARCH_QUERY', payload: e.target.value });
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMenuOpen(false);
  };

  // Determine the current page based on the URL path
  const currentPage = (() => {
    if (location.pathname === '/') return 'home';
    if (location.pathname.startsWith('/shop')) return 'shop';
    if (location.pathname.startsWith('/categories')) return 'categories';
    if (location.pathname.startsWith('/about')) return 'about';
    if (location.pathname.startsWith('/contact')) return 'contact';
    return '';
  })();

  return (
    <nav className="bg-pink-50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Navbar */}
        <div className="flex items-center justify-between h-16">
          {/* Logo - Always visible */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              <img src={Logo1} alt="Shynerz Logo" className="h-8 mr-2 mt-1" />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={() => handleNavigation('/shop')}
              className={`px-3 py-2 rounded-md ${
                currentPage === 'shop'
                  ? 'bg-pink-100 text-pink-600'
                  : 'text-gray-600 hover:bg-pink-50'
              }`}
            >
              Shop
            </button>
            <button
              onClick={() => handleNavigation('/categories')}
              className={`px-3 py-2 rounded-md ${
                currentPage === 'categories'
                  ? 'bg-pink-100 text-pink-600'
                  : 'text-gray-600 hover:bg-pink-50'
              }`}
            >
              Categories
            </button>
            <button
              onClick={() => handleNavigation('/about')}
              className={`px-3 py-2 rounded-md ${
                currentPage === 'about'
                  ? 'bg-pink-100 text-pink-600'
                  : 'text-gray-600 hover:bg-pink-50'
              }`}
            >
              About Us
            </button>
            <button
              onClick={() => handleNavigation('/contact')}
              className={`px-3 py-2 rounded-md ${
                currentPage === 'contact'
                  ? 'bg-pink-100 text-pink-600'
                  : 'text-gray-600 hover:bg-pink-50'
              }`}
            >
              Contact
            </button>
          </div>

          {/* Desktop Search */}
          <div className="hidden md:block flex-1 max-w-xl px-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Search products..."
                className="w-full px-4 py-2 rounded-full border-2 border-pink-200 focus:outline-none focus:border-pink-500"
                value={state.searchQuery}
                onChange={handleSearch}
              />
              <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center space-x-4">
            <a
              href="tel:+254759409139"
              className="flex items-center gap-2 text-gray-600 hover:text-pink-500"
            >
              <Phone className="h-5 w-5" />
              <span>+254 759 409139</span>
            </a>
            <button
              onClick={onWishlistClick}
              className="p-2 rounded-full hover:bg-pink-100 relative"
            >
              <Heart className="h-6 w-6 text-pink-500" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>
            <button
              onClick={onCartClick}
              className="p-2 rounded-full hover:bg-pink-100 relative"
            >
              <ShoppingCart className="h-6 w-6 text-pink-500" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {cartItemsCount}
                </span>
              )}
            </button>
          </div>

          {/* Mobile Actions */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={onWishlistClick}
              className="p-2 rounded-full hover:bg-pink-100 relative"
            >
              <Heart className="h-6 w-6 text-pink-500" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>
            <button
              onClick={onCartClick}
              className="p-2 rounded-full hover:bg-pink-100 relative"
            >
              <ShoppingCart className="h-6 w-6 text-pink-500" />
              {cartItemsCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {cartItemsCount}
                </span>
              )}
            </button>
            {/* Only show hamburger menu if not in standalone mode */}
            {!isStandalone && (
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 rounded-full hover:bg-pink-100"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? (
                  <X className="h-6 w-6 text-pink-500" />
                ) : (
                  <Menu className="h-6 w-6 text-pink-500" />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu - Only show if not in standalone mode */}
        {!isStandalone && isMenuOpen && (
          <div className="md:hidden border-t border-pink-100">
            <div className="px-2 py-3 space-y-3">
              {/* Mobile Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search products..."
                  className="w-full px-4 py-2 rounded-full border-2 border-pink-200 focus:outline-none focus:border-pink-500"
                  value={state.searchQuery}
                  onChange={handleSearch}
                />
                <Search className="absolute right-3 top-2.5 h-5 w-5 text-gray-400" />
              </div>

              {/* Mobile Navigation */}
              <button
                onClick={() => handleNavigation('/')}
                className={`w-full text-left px-3 py-2 rounded-md ${
                  currentPage === 'home'
                    ? 'bg-pink-100 text-pink-600'
                    : 'text-gray-600 hover:bg-pink-50'
                }`}
              >
                Home
              </button>
              <button
                onClick={() => handleNavigation('/shop')}
                className={`w-full text-left px-3 py-2 rounded-md ${
                  currentPage === 'shop'
                    ? 'bg-pink-100 text-pink-600'
                    : 'text-gray-600 hover:bg-pink-50'
                }`}
              >
                Shop
              </button>
              <button
                onClick={() => handleNavigation('/categories')}
                className={`w-full text-left px-3 py-2 rounded-md ${
                  currentPage === 'categories'
                    ? 'bg-pink-100 text-pink-600'
                    : 'text-gray-600 hover:bg-pink-50'
                }`}
              >
                Categories
              </button>
              <button
                onClick={() => handleNavigation('/about')}
                className={`w-full text-left px-3 py-2 rounded-md ${
                  currentPage === 'about'
                    ? 'bg-pink-100 text-pink-600'
                    : 'text-gray-600 hover:bg-pink-50'
                }`}
              >
                About Us
              </button>
              <button
                onClick={() => handleNavigation('/contact')}
                className={`w-full text-left px-3 py-2 rounded-md ${
                  currentPage === 'contact'
                    ? 'bg-pink-100 text-pink-600'
                    : 'text-gray-600 hover:bg-pink-50'
                }`}
              >
                Contact
              </button>

              {/* Mobile Phone */}
              <a
                href="tel:+254759409139"
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-pink-500"
              >
                <Phone className="h-5 w-5" />
                <span>+254 759 409139</span>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
